import React from 'react';
import { useField } from 'informed';

import { useStateAndDistricts } from '../../hooks/useStateAndDistricts';
import { getDistrictDetailsFromDistrictId } from '../../utils/ComboBoxUtils';

import { DistrictsComboBox } from '../DistrictsComboBox';
import { DistrictComboBoxProps } from '../DistrictsComboBox/DistrictsComboBox';
import { District } from '../types';

import { FieldContainer } from './styledComponents';

export interface InformedDistrictsComboboxProps
  extends Omit<DistrictComboBoxProps, 'onStateChange'> {
  stateId: string;
  isHidden?: boolean;
  field: string;
  validationSchemaObject: any;
  touched: boolean;
  isDisabled?: boolean;
  reRenderObjects?: Array<any>;
  fieldWrapperStyles?: React.CSSProperties;
  initialDistrictValue?: string;
  getDisplayValueFromSelectedState?: (T: any) => string;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  helperText?: string;
  isMarginBottomZero?: boolean;
  hintClassName?: string;
  enableDataMask?: boolean;
  placeHolder?: string;
  mobilePlaceholder?: string;
}

export const InformedDistrictsCombobox = (
  props: InformedDistrictsComboboxProps
) => {
  const {
    isHidden,
    field,
    validationSchemaObject,
    fieldWrapperStyles,
    touched,
    onSelectionChange,
    isDisabled,
    label,
    error,
    stateId,
    onBlur: onBlurPropFunc,
    reRenderObjects,
    initialDistrictValue,
    getDisplayValueFromSelectedState,
    validateOnChange = true,
    validateOnBlur = true,
    helperText,
    isMarginBottomZero,
    hintClassName,
    enableDataMask,
    placeHolder,
    ...comboboxProps
  } = props;

  const { fieldState, render, informed } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    stateId,
    ...reRenderObjects
  });

  const { value } = fieldState;
  const { onChange, onBlur } = informed;

  const onBlurFunction = (e) => {
    onBlur(e);
    onBlurPropFunc && onBlurPropFunc(e);
  };

  const { districts } = useStateAndDistricts();

  const onSelectionChangeFunc = (value) => {
    const district = getDistrictDetailsFromDistrictId(value, districts.current);
    onChange(district);
    onSelectionChange && onSelectionChange(district?.district_id);
  };

  //NOTE: we should not clear the district value initially when the form is rendered
  const onStateChange = () => {
    if (!Boolean(!(value as District)?.district_id && initialDistrictValue)) {
      onChange({});
    }
  };

  const renderDistrictsCombobox = () => {
    return (
      <DistrictsComboBox
        selectedKey={(value as District)?.district_id}
        label={label}
        stateId={stateId}
        onBlur={onBlurFunction}
        isDisabled={isDisabled}
        onSelectionChange={onSelectionChangeFunc}
        error={error}
        initialDistrictValue={initialDistrictValue}
        onStateChange={onStateChange}
        getDisplayValueFromSelectedState={getDisplayValueFromSelectedState}
        helperText={helperText}
        hintClassName={hintClassName}
        enableDataMask={enableDataMask}
        placeholder={placeHolder}
        {...comboboxProps}
      />
    );
  };

  return render(
    <FieldContainer
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderDistrictsCombobox()}
    </FieldContainer>
  );
};
